import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  type Kunde,
  type Uebernahme,
  akte as holeAkte,
  liste as holeListe,
  zusammenfuehren,
} from '../api/kunden';
import { ApiFehler } from '../api/fehler';
import { Abschnitt } from '../komponenten/Akte';
import { Erfassung } from '../komponenten/Erfassung';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';

/** Die Felder, über die feldweise entschieden wird (M-18). */
const FELDER = [
  { schluessel: 'firmierung', beschriftung: 'Firmierung' },
  { schluessel: 'rechtsform', beschriftung: 'Rechtsform' },
  { schluessel: 'ustidnr', beschriftung: 'USt-IdNr.' },
  { schluessel: 'branche', beschriftung: 'Branche' },
  { schluessel: 'herkunft', beschriftung: 'Herkunft' },
] as const;

type Feldschluessel = (typeof FELDER)[number]['schluessel'];

const zeitform = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'long',
  timeStyle: 'short',
  timeZone: 'Europe/Berlin',
});

/**
 * M-18 Dubletten zusammenführen (KUN-23, KUN-25, SYS-16).
 *
 * Gegenüberstellung zweier Datensätze, feldweise Übernahme, Vorschau der
 * Folgen, 30 Tage rücknehmbar.
 *
 * DER FÜHRENDE DATENSATZ BLEIBT, der unterlegene wird umgeleitet. Nichts wird
 * umgeschrieben — genau das macht die Rücknahme möglich: Nach einem Umschreiben
 * aller Fremdschlüssel wüsste niemand mehr, welche Belege ursprünglich zu
 * welchem der beiden gehörten.
 *
 * DIE VORSCHAU ZEIGT, WAS MITGEHT, und nicht, was verschwindet. Ansprechpartner
 * und Adressen des unterlegenen Datensatzes bleiben an ihm hängen und sind über
 * die Umleitung erreichbar; sie werden nicht kopiert. Wer das anders erwartet,
 * soll es vorher lesen und nicht hinterher suchen.
 *
 * „ZUSAMMENFÜHREN …" FRAGT IM POP-UP NACH (CRM-104): Der unterlegene
 * Datensatz verschwindet sofort aus Liste und Suche, und die Rücknahme ist
 * ein eigener Gang. Kein Eingriffsknopf — es bleibt 30 Tage rücknehmbar und
 * ist der Abschluss der Erfassung.
 */
export function DublettenZusammenfuehren({
  ziel,
  beiFertig,
  beiAbbruch,
}: {
  readonly ziel: string;
  readonly beiFertig: (ruecknahmeBis: string) => void;
  readonly beiAbbruch: () => void;
}): JSX.Element {
  const [zielAkte, setzeZielAkte] = useState<Kunde | undefined>(undefined);
  const [quelleAkte, setzeQuelleAkte] = useState<Kunde | undefined>(undefined);
  const [suche, setzeSuche] = useState('');
  const [treffer, setzeTreffer] = useState<readonly { id: string; name: string; nummer: string }[]>(
    [],
  );
  const [wahl, setzeWahl] = useState<Record<string, 'ziel' | 'quelle'>>({});
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [fragt, setzeFragt] = useState(false);

  useEffect(() => {
    void holeAkte(ziel)
      .then(setzeZielAkte)
      .catch((u: unknown) => setzeFehler(u as ApiFehler));
  }, [ziel]);

  useEffect(() => {
    if (suche.trim().length < 3) {
      setzeTreffer([]);
      return;
    }
    const kennung = setTimeout(() => {
      void holeListe({ suche })
        .then((e) =>
          setzeTreffer(
            e.kunden
              .filter((k) => k.id !== ziel)
              .slice(0, 8)
              .map((k) => ({ id: k.id, name: k.name, nummer: k.kundennummer })),
          ),
        )
        .catch(() => setzeTreffer([]));
    }, 250);
    return () => clearTimeout(kennung);
  }, [suche, ziel]);

  const waehleQuelle = useCallback((id: string): void => {
    void holeAkte(id)
      .then((k) => {
        setzeQuelleAkte(k);
        setzeTreffer([]);
        setzeSuche('');
        // Vorbelegung: Der führende Datensatz behält, was er hat. Der
        // unterlegene füllt nur, was am führenden leer ist — das ist der Fall,
        // den man in neun von zehn Zusammenführungen will.
        setzeWahl({});
      })
      .catch((u: unknown) => setzeFehler(u as ApiFehler));
  }, []);

  if (zielAkte === undefined) {
    return fehler !== undefined ? <Meldung fehler={fehler} /> : <p role="status">Wird geladen …</p>;
  }

  const wert = (k: Kunde | undefined, feld: Feldschluessel): string =>
    ((k as unknown as Record<string, unknown> | undefined)?.[feld] as string | null | undefined)
    ?? '—';

  /** Die Felder, die der unterlegene Datensatz an den führenden abgibt. */
  const gewaehlt = FELDER.filter((f) => wahl[f.schluessel] === 'quelle');

  /*
   * Wirft (CRM-104): Aus der Rückfrage heraus steht ein Fehler im Pop-up, und
   * es bleibt offen — die Seite meldet ihn nicht ein zweites Mal.
   */
  const ausfuehren = async (quelle: Kunde): Promise<void> => {
    const uebernehmen: Record<string, unknown> = {};
    for (const f of gewaehlt) {
      uebernehmen[f.schluessel] = (quelle as unknown as Record<string, unknown>)[f.schluessel];
    }
    const e = await zusammenfuehren(ziel, quelle.id, uebernehmen as Uebernahme);
    setzeFragt(false);
    beiFertig(e.ruecknahmeBis);
  };

  return (
    /*
     * ÄA-41, Seitentyp Erfassung: eine Karte, „Zusammenführen" oben rechts
     * und am Ende. Solange der unterlegene Datensatz fehlt, ist der Knopf
     * gesperrt — der Abbruch steht von Anfang an da.
     */
    <div className="maske maske--weit erfassung">
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <Erfassung
        beschriftung="Dubletten zusammenführen"
        beiAbsenden={() => {
          // Vor der Wahl des unterlegenen Datensatzes gibt es nichts zusammenzuführen;
          // die Eingabetaste im Suchfeld sendet das Formular trotzdem ab.
          if (quelleAkte !== undefined) setzeFragt(true);
        }}
        absenden={{ text: 'Zusammenführen …', gesperrt: quelleAkte === undefined }}
        abbrechen={{ text: 'Abbrechen', beiKlick: beiAbbruch }}
      >
        <Abschnitt titel="Datensätze" zusatz={zielAkte.kundennummer}>
          <p className="hilfe">
            Der <strong>führende</strong> Datensatz bleibt bestehen, der unterlegene wird auf ihn
            umgeleitet. Fremdschlüssel werden nicht umgeschrieben — deshalb ist die
            Zusammenführung 30 Tage rücknehmbar.
          </p>

          <dl className="angaben">
            <dt>Führend bleibt</dt>
            <dd>
              <strong>{zielAkte.name}</strong> · {zielAkte.kundennummer}
            </dd>
            {quelleAkte !== undefined && (
              <>
                <dt>Umgeleitet wird</dt>
                <dd>
                  <strong>{quelleAkte.name}</strong> · {quelleAkte.kundennummer}
                </dd>
              </>
            )}
          </dl>

          {quelleAkte === undefined && (
            <div className="unterblock">
              <label className="feld__beschriftung" htmlFor="m18-suche">
                Welcher Datensatz wird umgeleitet?
              </label>
              <input
                id="m18-suche"
                className="feld__eingabe"
                type="search"
                value={suche}
                onChange={(e) => setzeSuche(e.target.value)}
                placeholder="Name, Nummer, Ort oder USt-IdNr."
              />
              <ul className="kontaktliste">
                {treffer.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className="liste__oeffnen liste__nummer"
                      onClick={() => waehleQuelle(t.id)}
                    >
                      {t.nummer}
                    </button>{' '}
                    {t.name}
                  </li>
                ))}
                {suche.trim().length >= 3 && treffer.length === 0 && (
                  <li>Kein weiterer Treffer.</li>
                )}
              </ul>
            </div>
          )}
        </Abschnitt>

        {quelleAkte !== undefined && (
          <>
            {/* KUN-25: der zweispaltige Vergleich — links führend, rechts umgeleitet. */}
            <Abschnitt titel="Gegenüberstellung">
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle">
                  <caption>
                    Feldweise entscheiden: Welcher Wert bleibt am führenden Datensatz stehen?
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Feld</th>
                      <th scope="col">
                        {zielAkte.kundennummer} <span className="marke">führend</span>
                      </th>
                      <th scope="col">{quelleAkte.kundennummer}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FELDER.map((f) => {
                      const gleich =
                        wert(zielAkte, f.schluessel) === wert(quelleAkte, f.schluessel);
                      return (
                        <tr key={f.schluessel}>
                          <th scope="row">{f.beschriftung}</th>
                          <td>
                            <label>
                              <input
                                type="radio"
                                name={`feld-${f.schluessel}`}
                                checked={(wahl[f.schluessel] ?? 'ziel') === 'ziel'}
                                disabled={gleich}
                                onChange={() =>
                                  setzeWahl((w) => ({ ...w, [f.schluessel]: 'ziel' }))
                                }
                              />{' '}
                              {wert(zielAkte, f.schluessel)}
                            </label>
                          </td>
                          <td>
                            <label>
                              <input
                                type="radio"
                                name={`feld-${f.schluessel}`}
                                checked={wahl[f.schluessel] === 'quelle'}
                                disabled={gleich}
                                onChange={() =>
                                  setzeWahl((w) => ({ ...w, [f.schluessel]: 'quelle' }))
                                }
                              />{' '}
                              {wert(quelleAkte, f.schluessel)}
                              {gleich && <span className="marke">gleich</span>}
                            </label>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Abschnitt>

            {/* Vorschau der Folgen — was mitgeht und was hängen bleibt. */}
            <Abschnitt titel="Folgen">
              <div className="streifen streifen--info">
                <strong>Was geschieht.</strong>
                <ul>
                  <li>
                    {quelleAkte.kundennummer} verschwindet aus Liste und Suche und zeigt auf{' '}
                    {zielAkte.kundennummer}.
                  </li>
                  <li>
                    {quelleAkte.kontakte.length} Ansprechpartner und{' '}
                    {quelleAkte.adressen.length} Anschriften bleiben am umgeleiteten Datensatz —
                    sie werden nicht kopiert.
                  </li>
                  {/* BP-16 */}
                  <li>Die Kundennummer {quelleAkte.kundennummer} bleibt vergeben.</li>
                  <li>30 Tage rücknehmbar; danach hilft nur die Neuanlage.</li>
                </ul>
              </div>
            </Abschnitt>
          </>
        )}
      </Erfassung>

      {fragt && quelleAkte !== undefined && (
        <Rueckfrage
          frage="Dubletten zusammenführen?"
          unterzeile={`${quelleAkte.kundennummer} auf ${zielAkte.kundennummer}`}
          tat="Zusammenführen"
          beiBestaetigen={() => ausfuehren(quelleAkte)}
          beiSchliessen={() => {
            setzeFragt(false);
          }}
        >
          <p>
            {quelleAkte.kundennummer} {quelleAkte.name} verschwindet aus Liste und Suche und zeigt
            auf {zielAkte.kundennummer} {zielAkte.name}.{' '}
            {gewaehlt.length === 0
              ? 'Am führenden Datensatz wird kein Feld überschrieben.'
              : `Übernommen ${gewaehlt.length === 1 ? 'wird' : 'werden'}: ${gewaehlt
                  .map((f) => f.beschriftung)
                  .join(', ')}.`}
          </p>
          {/* KUN-25, SYS-16 */}
          <p>30 Tage rücknehmbar; danach hilft nur die Neuanlage.</p>
        </Rueckfrage>
      )}
    </div>
  );
}

/** Formatiert die Rücknahmefrist als Datum mit Uhrzeit (FRS-16). */
export function alsFrist(iso: string): string {
  return zeitform.format(new Date(iso));
}
